/**
 * Split a free-form weight string (typically pulled from a DICOM tag or a
 * legacy submission record) into a numeric value + unit pair the unit-aware
 * input can consume.
 *
 * Behaviour:
 *   - empty / nullish      → { value: '', unit: 'kg' }
 *   - "25"                 → { value: '25', unit: 'kg' }   (no unit defaults to kg)
 *   - "25 kg" / "25kg"     → { value: '25', unit: 'kg' }
 *   - "25 lb" / "25 lbs"   → { value: '25', unit: 'lb' }   ("lbs" is common in US DICOM)
 *   - unparseable garbage  → { value: <raw>, unit: 'kg' }  (keep the raw text, default unit)
 *
 * The unit detection is intentionally lenient — anything that isn't an
 * `lb`/`lbs` suffix falls back to kg rather than rejecting it, so we don't
 * blank out a user's weight just because the original string had odd
 * whitespace or capitalisation.
 */
export function parseWeight(raw) {
    const input = (raw ?? '').trim();
    if (!input)
        return { value: '', unit: 'kg' };
    const match = input.match(/^([\d.]+)\s*([a-zA-Z]+)?$/);
    if (!match)
        return { value: input, unit: 'kg' };
    const value = match[1];
    const unitRaw = (match[2] ?? '').toLowerCase();
    if (unitRaw === 'lb' || unitRaw === 'lbs')
        return { value, unit: 'lb' };
    return { value, unit: 'kg' };
}
/**
 * Inverse of parseWeight: render a value + unit pair back to the
 * "<value> <unit>" form we persist. Returns null for a blank value so
 * callers can pass straight through to a nullable API field.
 */
export function formatWeight(value, unit) {
    const trimmed = value.trim();
    if (!trimmed)
        return null;
    return `${trimmed} ${unit}`;
}
